/**
 * ForHRDirectorsPage — the public /for/hr-directors archetype landing page.
 *
 * Ported from the locked design
 * design/mockups/V1_Landing_For_HR_Directors_v1.html (Template 2 instance,
 * source: V1_Archetype_Landing_Pages.md Page 3).
 *
 * The app shell's <TopNav> renders the nav globally; this page is the
 * mockup content below <nav class="marketing-nav"> plus the canonical
 * footer. Scoped <style> block (media queries can't live in inline
 * styles); colors alias onto locked --ppr-* tokens. Unique class prefix
 * ppr-hrdir- so the global <style> never collides with sibling marketing
 * pages.
 *
 * Indexable page — usePageMeta only, no useNoindex.
 */

import { Link } from "react-router-dom";
import { usePageMeta } from "../../lib/use-page-meta.js";

const HRDIR_DESCRIPTION =
  "The interview, the performance review, the conflict mediation, the exit — PeoplePrints gives HR Directors a tactical read for every conversation, with an audit trail. From $349/month.";

const FAQS = [
  {
    q: "Can PeoplePrints help with conflict between two employees?",
    a: "Yes. The Conflict Mediation Blueprint generates a Predisposition Profile for each party and one mediation sequence — what to surface to whom, in what order, and where the convergence point is. It is a first-class V1 feature; no other major HR-adjacent tool has a conflict-mediation-specific surface.",
  },
  {
    q: "Does this require employees to take an assessment?",
    a: "No. PeoplePrints generates Profiles from minimal common data points already on file. The default operating mode also includes a consent-confirmation flow for the people being read.",
  },
  {
    q: "Is this EEOC-compliant? Does it make hiring decisions?",
    a: "PeoplePrints is a pre-interaction tool. It does not produce a hire / no-hire score, does not make automated decisions, and is not a substitute for EEOC-compliant evaluation. The decision and documentation stay with you. The full posture is at /security-and-privacy.",
  },
  {
    q: "What's the audit trail?",
    a: "Every Profile generation, every Blueprint, and every admin action is logged. The Fairness Audit surface gives you visibility into how Profiles are used across the org.",
  },
  {
    q: "What does it cost?",
    a: "The HR Director tier is $349/month for up to 200 Profiles. Enterprise (multi-seat) is quote-based.",
  },
];

// FAQPage structured data — derived from FAQS so the schema text stays
// verbatim-matched to the on-page FAQ (a Google requirement for FAQ rich
// results). Mirrors the locked pattern in PricingPage.jsx.
const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const FOOTER_LINKS = [
  { to: "/#how-it-works", label: "Product" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/for/recruiters", label: "For Recruiters" },
  { to: "/for/managers", label: "For Managers" },
  { to: "/for/hr-directors", label: "For HR Directors" },
  { to: "/for/founders", label: "For Founders" },
  { to: "/for/employee-relations", label: "For Employee Relations" },
  { to: "/methodology", label: "Methodology" },
  { to: "/about", label: "About" },
  { to: "/security-and-privacy", label: "Security & Privacy" },
  { to: "/demo", label: "Request a Demo" },
];

export default function ForHRDirectorsPage() {
  usePageMeta({
    title:
      "One Engine for Every Conversation HR Owns — PeoplePrints for HR Directors",
    description: HRDIR_DESCRIPTION,
    canonicalPath: "/for/hr-directors",
  });

  return (
    <div className="ppr-hrdir-root">
      <style>{HRDIR_CSS}</style>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSONLD) }}
      />

      {/* ── BLOCK 2 — HERO ── */}
      <section className="ppr-hrdir-hero">
        <div className="ppr-hrdir-hero-inner">
          <p className="ppr-hrdir-hero-eyebrow">
            For HR Directors &amp; VPs of People
          </p>
          <h1 className="ppr-hrdir-hero-h1">
            The interview, the review, the mediation, the exit — one engine for
            every conversation HR owns.
          </h1>
          <p className="ppr-hrdir-hero-sub">
            You own the full conversation surface, and every conversation on it
            is high-stakes and low-data. PeoplePrints gives you a tactical read
            for each one — generated from data you already have, delivered as a
            per-conversation Blueprint, with an audit trail underneath.
          </p>
        </div>
      </section>

      {/* ── BLOCK 3 — EXTRACTABLE DEFINITION ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact">
        <div className="ppr-hrdir-block-inner">
          <div className="ppr-hrdir-definition">
            <p className="ppr-hrdir-def-label">
              What PeoplePrints does for HR Directors
            </p>
            <p className="ppr-hrdir-def-text">
              PeoplePrints is a pre-interaction intelligence platform for HR
              Directors. It generates a{" "}
              <strong>Predisposition Profile</strong> for any candidate or
              employee from minimal common data points, then produces a
              per-conversation Blueprint for the full HR surface: interviews,
              performance reviews, conflict mediations, PIPs, stay interviews,
              skip-levels, and exit interviews. It includes a Fairness Audit
              surface for visibility into how Profiles are used inside the
              organization, plus tenant admin controls and an audit log.
            </p>
          </div>
        </div>
      </section>

      {/* ── BLOCK 4 — THE PAIN ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact">
        <div className="ppr-hrdir-block-inner">
          <p className="ppr-hrdir-block-eyebrow">Sound familiar?</p>
          <h2 className="ppr-hrdir-block-h2">
            You own all of it — and every conversation is low-data.
          </h2>
          <p className="ppr-hrdir-pain-quote">
            Interviews, performance calibration, the conflict between two senior
            contributors, the PIP, the exit interview, the documentation that
            has to hold up if it's ever questioned. You own all of it. You can't
            run an assessment on a candidate mid-loop, on two employees in
            conflict, or on a departing employee — and the tools you have don't
            read people before the conversation.
          </p>
        </div>
      </section>

      {/* ── BLOCK 5 — THE SHIFT ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact ppr-hrdir-tinted">
        <div className="ppr-hrdir-block-inner">
          <p className="ppr-hrdir-block-eyebrow">The shift</p>
          <h2 className="ppr-hrdir-block-h2">
            Every conversation on the HR surface comes with a read.
          </h2>
          <p className="ppr-hrdir-block-body">
            With PeoplePrints, every conversation on the HR surface comes with a
            read. The Conflict Mediation Blueprint generates two Profiles and
            one mediation sequence. The Performance Review variants — PIP, stay
            interview, skip-level, exit — each get a calibrated Blueprint. The
            Fairness Audit gives you visibility into how Profiles are being used
            across the org. The audit log gives you the trail.
          </p>
        </div>
      </section>

      {/* ── BLOCK 6 — WHAT YOU GET (In-This-Blueprint card grammar) ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact">
        <div className="ppr-hrdir-block-inner">
          <h2 className="ppr-hrdir-block-h2">
            The Blueprints that matter to an HR Director.
          </h2>
          <div className="ppr-hrdir-itb-card">
            <div className="ppr-hrdir-itb-stripe" />
            <div className="ppr-hrdir-itb-inner">
              <p className="ppr-hrdir-itb-label">What you get</p>
              <ol className="ppr-hrdir-itb-list">
                <li>
                  <strong>
                    A Predisposition Profile for any candidate or employee
                  </strong>{" "}
                  — no assessment, generated from data you already have.
                </li>
                <li>
                  <strong>The full Blueprint library</strong> — interview,
                  performance review, Conflict Mediation, PIP, stay interview,
                  skip-level, exit interview.
                </li>
                <li>
                  <strong>The Conflict Mediation Blueprint</strong> — two
                  Profiles, one mediation sequence. No other HR tool has a
                  conflict-mediation-specific surface.
                </li>
                <li>
                  <strong>The Fairness Audit</strong> — visibility into how
                  Profiles get used inside your org.
                </li>
                <li>
                  <strong>Tenant admin controls + audit log</strong> —
                  role-based access and a full activity trail.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 7 — HOW IT WORKS ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact ppr-hrdir-tinted">
        <div className="ppr-hrdir-block-inner">
          <p className="ppr-hrdir-block-eyebrow">How it works</p>
          <h2 className="ppr-hrdir-block-h2">
            Minimal data in. Profile. Blueprint.
          </h2>
          <div className="ppr-hrdir-step-list">
            <div className="ppr-hrdir-step-row">
              <div className="ppr-hrdir-step-num">1</div>
              <p className="ppr-hrdir-step-text">
                <strong>Enter the person's common data points.</strong>
              </p>
            </div>
            <div className="ppr-hrdir-step-row">
              <div className="ppr-hrdir-step-num">2</div>
              <p className="ppr-hrdir-step-text">
                <strong>The engine generates the Predisposition Profile.</strong>
              </p>
            </div>
            <div className="ppr-hrdir-step-row">
              <div className="ppr-hrdir-step-num">3</div>
              <p className="ppr-hrdir-step-text">
                <strong>
                  Open the Blueprint for the specific conversation
                </strong>{" "}
                — and run it with the read done.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── BLOCK 8 — PRICING POINTER ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact">
        <div className="ppr-hrdir-block-inner">
          <div className="ppr-hrdir-price-pointer">
            <p className="ppr-hrdir-pp-tier">HR Director — $349 / month</p>
            <p className="ppr-hrdir-pp-body">
              The HR Director tier is built for you: 200 Profiles per month, the
              full Blueprint library including Conflict Mediation, the Fairness
              Audit, tenant admin controls and audit log —{" "}
              <strong>$349/month</strong>. Rolling out beyond a single HR seat?
              Talk to us about Enterprise.
            </p>
          </div>
          <p className="ppr-hrdir-xlink">
            Conflict mediation is the core of your role? See{" "}
            <Link to="/for/employee-relations">
              PeoplePrints for employee relations →
            </Link>
          </p>
        </div>
      </section>

      {/* ── BLOCK 9 — FAQ ── */}
      <section className="ppr-hrdir-block ppr-hrdir-compact ppr-hrdir-tinted">
        <div className="ppr-hrdir-block-inner">
          <p className="ppr-hrdir-block-eyebrow">FAQ</p>
          <h2 className="ppr-hrdir-block-h2">Questions HR Directors ask.</h2>
          <div className="ppr-hrdir-faq-list">
            {FAQS.map((f) => (
              <div className="ppr-hrdir-faq-item" key={f.q}>
                <h3 className="ppr-hrdir-faq-q">{f.q}</h3>
                <p className="ppr-hrdir-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOCK 10 — CREDENTIALING STRIP ── */}
      <section className="ppr-hrdir-cred-strip">
        <div className="ppr-hrdir-cred-strip-inner">
          <span className="ppr-hrdir-cs-label">The engine</span>
          PeoplePrints is built on a proprietary behavioral-prediction engine.
          Founder Russ Cersosimo brings three decades of applied behavioral
          strategy, a biopharma patent, and continuing-education curricula
          accredited for lawyers and physicians. The full methodology is at{" "}
          <Link to="/methodology">/methodology</Link>; the founder bio is at{" "}
          <Link to="/about">/about</Link>.
        </div>
      </section>

      {/* ── BLOCK 11 — CTA ── */}
      <section className="ppr-hrdir-cta-band">
        <div className="ppr-hrdir-cta-band-inner">
          <h2 className="ppr-hrdir-cta-band-h">
            One engine for every conversation HR owns.
          </h2>
          <div className="ppr-hrdir-cta-actions">
            <Link
              to="/demo"
              className="ppr-hrdir-cta-btn ppr-hrdir-cta-primary"
            >
              Request a demo
            </Link>
            <Link
              to="/demo"
              className="ppr-hrdir-cta-btn ppr-hrdir-cta-secondary"
            >
              Book a 30-minute demo
            </Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER — canonical site-wide footer ── */}
      <footer className="ppr-hrdir-footer">
        <div className="ppr-hrdir-footer-inner">
          <div className="ppr-hrdir-footer-links">
            {FOOTER_LINKS.map((l) => (
              <Link to={l.to} key={l.to}>
                {l.label}
              </Link>
            ))}
          </div>
          <div className="ppr-hrdir-footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <p className="ppr-hrdir-footer-copy">
            © 2026 PeoplePrints™. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

const HRDIR_CSS = `
.ppr-hrdir-root {
  --c-navy: var(--ppr-color-accent-navy);
  --c-steel: var(--ppr-color-accent-steel);
  --c-steel-dark: var(--ppr-color-accent-steel-dark);
  --c-powder: var(--ppr-color-accent-powder);
  --c-pale: var(--ppr-color-accent-pale);
  --c-pale-soft: var(--ppr-color-accent-pale-soft);
  --c-coral: var(--ppr-color-accent-coral);
  --c-coral-dark: var(--ppr-color-accent-coral-dark);
  --c-coral-very-soft: var(--ppr-color-accent-coral-very-soft);
  /* --c-gold: the locked marketing mockups use a gold hero-eyebrow accent
     with no token in design-tokens.v2.json — ported as a literal, flagged
     for design. */
  --c-gold: #C99A4B;
  --c-surface: var(--ppr-color-background-surface);
  --c-text: var(--ppr-color-text-primary);
  --c-text-2: var(--ppr-color-text-secondary);
  --c-muted: var(--ppr-color-text-muted);
  --c-border: var(--ppr-color-border-default);
  --f-display: var(--ppr-typography-font-family-display);
  --f-body: var(--ppr-typography-font-family-body);
  --f-ui: var(--ppr-typography-font-family-ui);
  --f-mono: var(--ppr-typography-font-family-mono);
  font-family: var(--f-body);
  color: var(--c-text);
  line-height: 1.55;
  background: var(--c-surface);
}
.ppr-hrdir-root * { box-sizing: border-box; }

.ppr-hrdir-block { padding: 64px 32px; }
.ppr-hrdir-block.ppr-hrdir-compact { padding: 44px 32px; }
.ppr-hrdir-block.ppr-hrdir-tinted { background: var(--c-pale-soft); }
.ppr-hrdir-block-inner { max-width: 940px; margin: 0 auto; }
.ppr-hrdir-block-eyebrow { font-family: var(--f-display); font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 14px; }
.ppr-hrdir-block-h2 { font-family: var(--f-display); font-size: 27px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 16px; }
.ppr-hrdir-block-body { font-family: var(--f-body); font-size: 16.5px; line-height: 1.65; color: var(--c-text); margin: 0 0 16px; }
.ppr-hrdir-block-body strong { color: var(--c-navy); }

.ppr-hrdir-hero { padding: 80px 32px 56px; background: linear-gradient(180deg, var(--c-pale-soft) 0%, var(--c-surface) 100%); }
.ppr-hrdir-hero-inner { max-width: 900px; margin: 0 auto; text-align: center; }
.ppr-hrdir-hero-eyebrow { font-family: var(--f-ui); font-size: 12px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: var(--c-gold); margin: 0 0 18px; }
.ppr-hrdir-hero-h1 { font-family: var(--f-display); font-size: 38px; line-height: 1.1; letter-spacing: -0.025em; color: var(--c-navy); margin: 0 0 20px; }
.ppr-hrdir-hero-sub { font-family: var(--f-body); font-size: 18px; line-height: 1.65; color: var(--c-text-2); margin: 0 auto; max-width: 720px; text-align: left; }

.ppr-hrdir-definition { background: var(--c-pale-soft); border: 1.5px solid var(--c-powder); border-left: 6px solid var(--c-navy); padding: 26px 30px; }
.ppr-hrdir-def-label { font-family: var(--f-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-steel-dark); margin: 0 0 10px; }
.ppr-hrdir-def-text { font-family: var(--f-body); font-size: 17px; line-height: 1.65; color: var(--c-text); margin: 0; }
.ppr-hrdir-def-text strong { color: var(--c-navy); }

.ppr-hrdir-pain-quote { font-family: var(--f-body); font-size: 17px; font-style: italic; line-height: 1.6; color: var(--c-text); border-left: 4px solid var(--c-coral); background: var(--c-coral-very-soft); padding: 20px 24px; margin: 0; }

.ppr-hrdir-itb-card { background: var(--c-surface); border: 3px solid var(--c-navy); margin: 6px 0 0; }
.ppr-hrdir-itb-stripe { height: 8px; background: var(--c-coral); }
.ppr-hrdir-itb-inner { padding: 24px 28px; }
.ppr-hrdir-itb-label { font-family: var(--f-display); font-size: 19px; color: var(--c-navy); text-transform: uppercase; letter-spacing: 0.04em; margin: 0 0 14px; }
.ppr-hrdir-itb-list { margin: 0; padding: 0; list-style: none; counter-reset: itb; }
.ppr-hrdir-itb-list li { position: relative; padding: 11px 0 11px 38px; font-family: var(--f-body); font-size: 15.5px; line-height: 1.55; border-bottom: 1px solid var(--c-border); counter-increment: itb; }
.ppr-hrdir-itb-list li:last-child { border-bottom: none; padding-bottom: 0; }
.ppr-hrdir-itb-list li::before { content: counter(itb); position: absolute; left: 0; top: 10px; width: 23px; height: 23px; background: var(--c-navy); color: #FFF; border-radius: 50%; font-family: var(--f-display); font-size: 11px; display: flex; align-items: center; justify-content: center; }
.ppr-hrdir-itb-list strong { color: var(--c-navy); }

.ppr-hrdir-step-list { margin: 20px 0 0; }
.ppr-hrdir-step-row { display: grid; grid-template-columns: 40px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid var(--c-border); }
.ppr-hrdir-step-row:last-child { border-bottom: none; }
.ppr-hrdir-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--c-navy); color: #FFF; font-family: var(--f-display); font-size: 13px; display: flex; align-items: center; justify-content: center; }
.ppr-hrdir-step-text { font-family: var(--f-body); font-size: 15.5px; line-height: 1.6; color: var(--c-text); margin: 4px 0 0; }
.ppr-hrdir-step-text strong { color: var(--c-navy); }

.ppr-hrdir-price-pointer { border: 2px solid var(--c-navy); border-top: 8px solid var(--c-coral); background: var(--c-surface); padding: 26px 30px; }
.ppr-hrdir-pp-tier { font-family: var(--f-display); font-size: 13px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--c-coral); margin: 0 0 8px; }
.ppr-hrdir-pp-body { font-family: var(--f-body); font-size: 16px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-hrdir-pp-body strong { color: var(--c-navy); }

.ppr-hrdir-xlink { font-family: var(--f-ui); font-size: 14px; color: var(--c-text-2); margin: 18px 0 0; }
.ppr-hrdir-xlink a { color: var(--c-coral-dark); font-weight: 600; text-decoration: none; }

.ppr-hrdir-faq-list { margin: 20px 0 0; }
.ppr-hrdir-faq-item { border: 1.5px solid var(--c-border); border-left: 4px solid var(--c-steel); margin: 0 0 12px; background: var(--c-surface); padding: 20px 24px; }
.ppr-hrdir-faq-q { font-family: var(--f-display); font-size: 15.5px; color: var(--c-navy); margin: 0 0 8px; letter-spacing: -0.005em; }
.ppr-hrdir-faq-a { font-family: var(--f-body); font-size: 15px; line-height: 1.6; color: var(--c-text); margin: 0; }
.ppr-hrdir-faq-a strong { color: var(--c-navy); }

.ppr-hrdir-cred-strip { background: var(--c-navy); color: #FFF; padding: 28px 32px; }
.ppr-hrdir-cred-strip-inner { max-width: 940px; margin: 0 auto; font-family: var(--f-body); font-size: 14.5px; line-height: 1.65; }
.ppr-hrdir-cred-strip-inner a { color: var(--c-powder); text-decoration: underline; }
.ppr-hrdir-cs-label { font-family: var(--f-display); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--c-powder); display: block; margin: 0 0 8px; }

.ppr-hrdir-cta-band { background: var(--c-pale-soft); padding: 60px 32px; text-align: center; }
.ppr-hrdir-cta-band-inner { max-width: 760px; margin: 0 auto; }
.ppr-hrdir-cta-band-h { font-family: var(--f-display); font-size: 24px; line-height: 1.14; letter-spacing: -0.02em; color: var(--c-navy); margin: 0 0 24px; }
.ppr-hrdir-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }
.ppr-hrdir-cta-btn { display: inline-block; padding: 14px 26px; border-radius: 6px; text-decoration: none; font-family: var(--f-display); font-size: 12.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.ppr-hrdir-cta-primary { background: var(--c-coral); color: #FFF; border: 2px solid var(--c-coral); }
.ppr-hrdir-cta-secondary { background: transparent; color: var(--c-navy); border: 2px solid var(--c-navy); }

.ppr-hrdir-footer { background: var(--c-surface); border-top: 1px solid var(--c-border); padding: 44px 32px; }
.ppr-hrdir-footer-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
.ppr-hrdir-footer-links { display: flex; gap: 20px; justify-content: center; flex-wrap: wrap; margin: 0 0 14px; font-family: var(--f-ui); font-size: 13px; font-weight: 600; }
.ppr-hrdir-footer-links a { color: var(--c-text-2); text-decoration: none; }
.ppr-hrdir-footer-legal { display: flex; gap: 16px; justify-content: center; margin: 0 0 16px; font-family: var(--f-ui); font-size: 12.5px; }
.ppr-hrdir-footer-legal a { color: var(--c-muted); text-decoration: none; }
.ppr-hrdir-footer-copy { font-family: var(--f-mono); font-size: 11.5px; color: var(--c-muted); }

@media (max-width: 800px) {
  .ppr-hrdir-hero-h1 { font-size: 28px; }
  .ppr-hrdir-block-h2 { font-size: 22px; }
}
`;
